/************************************************************************************

* [GENERAL COMPONENTS]

*************************************************************************************/

/*****************************************
 * [COMPONENT]: COLLAPSIBLE TAB
 ******************************************/

class LTACollapsibleTab extends HTMLElement {
	constructor() {
		super()
	}

	connectedCallback() {
		// Elements
		this.triggerEl = this.querySelector('[data-trigger]')
		this.contentEl = this.querySelector('[data-content]')

		// Data
		this.expandedClass = 'is-expanded'
		this.collapsedClass = 'is-collapsed'
		this.collapsedHeight = '0px'
		this.oneAtATime = true
		this.isScrollIntoView = this.dataset.scrollIntoView === 'true'

		this.transitionendEvent = this.transitionendEventName()

		// Setup default behavior / data
		this.dataset.oneOpen === 'false' && (this.oneAtATime = false)

		if (this.getAttribute('open') === 'true') {
			this.selected = true
			this.contentEl.removeAttribute('hidden')
			this.contentEl.classList.remove('l-hidden')
			this.classList.add(this.expandedClass)
			this.setExpandedAria(false)
		} else {
			this.contentEl.style.height = this.collapsedHeight
			this.classList.add(this.collapsedClass)
			this.setCollapsedAria(null, false)
		}

		// Events
		this.triggerEl.addEventListener('click', (e) => this.toggle(e))
	}

	toggle(e) {
		e.preventDefault()

		this.selected ? this.close() : this.open()

		/* Close another tab and open only the current tab. */
		if (this.selected && this.oneAtATime) {
			const tabsContainer = this.closest('[data-collapsible-wrapper]')
			const collapsibleTabs = tabsContainer?.querySelectorAll('l-collapsible-tab')

			collapsibleTabs?.length &&
				collapsibleTabs.forEach((tab) => {
					tab !== this && tab.selected && tabsContainer !== tab && tab.close()
				})
		}
	}

	open() {
		this.selected = true
		this.expand()

		this.setExpandedAria()
	}

	close() {
		this.selected = false
		this.collapse()

		this.setCollapsedAria()
	}

	expand() {
		const onTransitionEnd = ((event) => {
			if (event.target === this.contentEl) {
				this.contentEl.removeEventListener(this.transitionendEvent, onTransitionEnd)

				this.isOpen &&
					requestAnimationFrame(() => {
						this.contentEl.style.transition = '0'
						this.contentEl.style.height = 'auto'

						requestAnimationFrame(() => {
							this.contentEl.style.height = null
							this.contentEl.style.transition = null
						})
					})
			}
		}).bind(this)

		this.contentEl.addEventListener(this.transitionendEvent, onTransitionEnd)
		this.isOpen = true

		this.contentEl.removeAttribute('hidden')
		this.contentEl.classList.remove('l-hidden')

		this.classList.add(this.expandedClass)
		this.classList.remove(this.collapsedClass)

		this.contentEl.style.height = this.contentEl.scrollHeight + 'px'
	}

	collapse() {
		const onTransitionEnd = ((event) => {
			if (event.target === this.contentEl) {
				this.contentEl.removeEventListener(this.transitionendEvent, onTransitionEnd)

				if (!this.isOpen) {
					this.classList.add(this.collapsedClass)
				}

				this.contentEl.setAttribute('hidden', 'hidden')
				this.contentEl.classList.add('l-hidden')
			}
		}).bind(this)

		this.contentEl.addEventListener(this.transitionendEvent, onTransitionEnd)
		this.isOpen = false
		this.classList.remove(this.expandedClass)

		requestAnimationFrame(() => {
			this.contentEl.style.transition = '0'
			this.contentEl.style.height = this.contentEl.scrollHeight + 'px'

			requestAnimationFrame(() => {
				this.contentEl.style.transition = null
				this.contentEl.style.height = this.collapsedHeight
			})
		})

		if (this.isScrollIntoView) {
			l_debounce(() => {
				this.scrollIntoView({ behavior: 'smooth', block: 'center' })
			}, 400)()
		}
	}

	destroy() {
		this.triggerEl && this.triggerEl.removeEventListener('click', (e) => this.toggle(e))
		this.contentEl && this.contentEl.removeAttribute('aria-hidden')
		this.contentEl && (this.contentEl.style.height = 'auto')

		this.classList.remove(this.expandedClass, this.collapsedClass)
		this.removeAttribute('open')
	}

	transitionendEventName() {
		let eventName,
			testElement = document.createElement('div'),
			eventNames = {
				transition: 'transitionend',
				OTransition: 'otransitionend',
				MozTransition: 'transitionend',
				WebkitTransition: 'webkitTransitionEnd',
			}

		for (eventName in eventNames) {
			if (eventNames.hasOwnProperty(eventName) && testElement.style[eventName] !== false) {
				return eventNames[eventName]
			}
		}
	}

	setExpandedAria(autoFocus = true) {
		this.setAttribute('open', true)

		this.triggerEl.setAttribute('aria-expanded', 'true')
		this.contentEl.removeAttribute('aria-hidden')
		this.contentEl.removeAttribute('tabindex')

		if (autoFocus) {
			l_enableFocusableElements(this.contentEl)
			l_trapFocus(this)
		}
	}

	setCollapsedAria(elementToFocus = this.triggerEl, enableTrapFocus = true) {
		this.removeAttribute('open')

		this.triggerEl.setAttribute('aria-expanded', 'false')
		this.contentEl.setAttribute('aria-hidden', 'true')
		this.contentEl.setAttribute('tabindex', '-1')

		l_disableFocusableElements(this.contentEl)
		enableTrapFocus && l_removeTrapFocus(elementToFocus)
	}
}

customElements.define('l-collapsible-tab', LTACollapsibleTab)

/*****************************************
 * [COMPONENT]: SWIPER CAROUSEL
 ******************************************/

class LTASwiperCarousel extends HTMLElement {
	constructor() {
		super()
		this._onResize = this._onResize.bind(this)
		this._swiperInitialized = false
	}

	connectedCallback() {
		if (window?.Swiper) {
			this.setupCarousel()
			return
		}

		document.addEventListener('l:vendor:loaded', () => {
			this.setupCarousel()
		})
	}

	disconnectedCallback() {
		window.removeEventListener('resize', this._onResize)
	}

	setupCarousel() {
		const configNode = this.querySelector('[data-carousel-config]')
		if (!configNode) return

		this.carouselConfig = JSON.parse(configNode.textContent)
		if (!this.carouselConfig) return

		this.swiperSlideEls = this.querySelectorAll('.swiper-slide')

		this.classList.remove('is-placeholder')
		this.swiperSlideEls.forEach((swiperSlide) => swiperSlide.classList.remove('l-hidden'))

		const swiperEl = this.querySelector('[data-swiper]') || this
		this.swiperCarousel = new window.Swiper(swiperEl, this.carouselConfig)
		this._swiperInitialized = true

		// Design mode scroll
		if (window.Shopify?.designMode) {
			this.designModeScroll()
		}

		// Set up navigation top position
		const hasNavigationTarget = this.carouselConfig?.navigation && this.carouselConfig.navigation.navigationHeightTarget
		if (hasNavigationTarget) {
			this.setupNavigation()
			window.addEventListener('resize', this._onResize)
		}

		// Dispatch event
		if (this.dataset.dispatchEvent === 'true') {
			this.dispatchEvent(
				new CustomEvent('l:swiper:carousel:loaded', {
					bubbles: true,
					detail: {
						id: this.id || this.dataset.id || 'default',
					},
				})
			)
		}
	}

	_onResize() {
		// Re-setup navigation when window resizes
		if (this._swiperInitialized && this.carouselConfig?.navigation?.navigationHeightTarget) {
			this.setupNavigation()
		}
	}

	setupNavigation() {
		const navigation = this.carouselConfig?.navigation
		if (!navigation || !navigation.navigationHeightTarget) return

		const targetEl = this.querySelector(navigation.navigationHeightTarget)
		if (!targetEl) return

		const navTop = `${targetEl.offsetHeight / 2}px`

		const nextNavEls = document.querySelectorAll(navigation.nextEl)
		const prevNavEls = document.querySelectorAll(navigation.prevEl)

		nextNavEls.forEach((el) => el.style.setProperty('--l-swiper-top', navTop))
		prevNavEls.forEach((el) => el.style.setProperty('--l-swiper-top', navTop))
	}

	designModeScroll() {
		document.addEventListener('shopify:block:select', (event) => {
			const targetEl = event.target

			this.swiperSlideEls.forEach((swiperSlide, index0) => {
				if (targetEl === swiperSlide) this.slideTo(index0)
			})
		})
	}

	slideTo(index = 0) {
		if (this.swiperCarousel && typeof this.swiperCarousel.slideTo === 'function') {
			this.swiperCarousel.slideTo(index)
		}
	}
}

customElements.define('l-swiper-carousel', LTASwiperCarousel)

/*****************************************
 * [COMPONENT]: MEDIA VIDEO
 ******************************************/

class LTAMediaVideo extends HTMLElement {
	constructor() {
		super()
	}

	connectedCallback() {
		// Elements
		this.videoContainerEl = this.querySelector('[data-video-container]') || this

		// Click
		this.addEventListener('click', (event) => {
			const hasLoaded = this.classList.contains('is-loaded')
			const isPlaying = this.classList.contains('is-playing')

			hasLoaded ? (isPlaying ? this.pauseVideo() : this.playVideo()) : this.initPlayer(event)
		})
	}

	initPlayer(event) {
		event.preventDefault()

		const templateEl = this.querySelector('template')

		if (!templateEl) return

		this.pauseOtherVideos()

		// Add template content to the element
		const templateContentEl = templateEl.content.cloneNode(true)

		this.videoContainerEl.appendChild(templateContentEl)
		this.classList.add('is-loaded', 'is-playing')
	}

	playVideo() {
		this.pauseOtherVideos()
		l_playMedia(this)

		this.classList.add('is-playing')
		this.classList.remove('is-paused')
	}

	pauseVideo() {
		l_pauseAllMedia(this)

		this.classList.remove('is-playing')
		this.classList.add('is-paused')
	}

	pauseOtherVideos() {
		l_pauseAllMedia(this.parentElement)

		this.parentElement.querySelectorAll('l-media-video').forEach((videoEl) => {
			const isLoaded = videoEl.classList.contains('is-loaded')

			if (isLoaded) {
				videoEl.classList.remove('is-playing')
				videoEl.classList.add('is-paused')
			}
		})
	}
}

customElements.define('l-media-video', LTAMediaVideo)

/*****************************************
 * [COMPONENT]:-DRAWER
 *****************************************/

class LTADrawer extends HTMLElement {
	constructor() {
		super()
	}

	connectedCallback() {
		this.setupDrawer()
	}

	setupDrawer() {
		// Elements
		this.triggerEls = document.querySelectorAll(this.dataset.triggerSelector)
		this.closeBtnEls = this.querySelectorAll('[data-close]')
		this.drawerInnerEl = this.querySelector('[data-drawer-inner]')
		this.triggerEl = null

		if (this.dataset.closeSelector) {
			const externalCloseBtnEls = document.querySelectorAll(this.dataset.closeSelector)
			this.closeBtnEls = Array.from(this.closeBtnEls).concat(Array.from(externalCloseBtnEls))
		}

		// Data
		this.onlyOneDrawerOpen = this.dataset.onlyOneDrawerOpen === 'true'
		this.drawerName = this.dataset.drawerName || 'drawer'

		// Setup events

		// Close this drawer if another drawer is open
		if (this.onlyOneDrawerOpen) {
			document.addEventListener('l:drawer:open', (event) => {
				if (event.detail.drawerName !== this.drawerName) {
					this.drawer_close()
				}
			})
		}

		// Open event
		this.triggerEls.forEach((triggerEl) => {
			triggerEl.addEventListener('click', (event) => {
				event.preventDefault()

				this.drawer_open(triggerEl)
			})
		})

		// Close event
		this.closeBtnEls.forEach((closeBtnEl) => {
			closeBtnEl.addEventListener('click', () => {
				this.drawer_close()
			})
		})
	}

	drawer_open(triggerEl) {
		l_setupVariablesBeforeOpenModal()
		l_lockBodyScroll(this.drawerName)

		// Open drawer
		this.classList.remove('l-hidden')

		requestAnimationFrame(() => {
			this.classList.add('is-active')
		})

		// Setup focus trap on main content
		if (triggerEl) {
			this.triggerEl = triggerEl
			l_trapFocus(this.drawerInnerEl, this.defaultElementToFocus)
		}

		// Set up accessibility
		this.setAttribute('aria-expanded', 'true')
		this.removeAttribute('tabindex')

		// Set global events
		window.addEventListener('click', this.drawer_closeOutsideOnClick.bind(this))
		window.addEventListener('keyup', this.drawer_closeOutsideOnEscape.bind(this))

		// Dispatch event to notify that the drawer is open
		document.dispatchEvent(
			new CustomEvent('l:drawer:open', {
				detail: { drawerName: this.drawerName },
			})
		)
	}

	drawer_close() {
		l_unLockBodyScroll(this.drawerName)

		// Close drawer
		this.classList.remove('is-active')

		window.l_debounce(() => {
			this.classList.add('l-hidden')
		}, 400)()

		// Remove focus trap
		if (this.triggerEl) {
			l_removeTrapFocus(this.triggerEl)
			this.triggerEl = null
		}

		// Remove accessibility
		this.setAttribute('aria-expanded', 'false')
		this.setAttribute('tabindex', '-1')

		// Remove global events
		window.removeEventListener('click', this.drawer_closeOutsideOnClick)
		window.removeEventListener('keyup', this.drawer_closeOutsideOnEscape)
	}

	drawer_closeOutsideOnClick(event) {
		if (!this.classList.contains('is-active')) return

		const target = event.target
		const triggerSelector = this.dataset.drawerTriggerSelector || 'l-drawer-trigger'
		const isDrawerTrigger = event.target.closest(triggerSelector) || event.target.tagName == triggerSelector

		const isOutsideClick =
			!this.drawerInnerEl.contains(target) &&
			!Array.from(this.triggerEls).some((el) => el.contains(target)) &&
			!isDrawerTrigger

		if (isOutsideClick) this.drawer_close()
	}

	drawer_closeOutsideOnEscape(event) {
		if (event.code.toUpperCase() === 'ESCAPE') {
			this.drawer_close()
		}
	}
}

customElements.define('l-drawer', LTADrawer)

/*****************************************
 * [COMPONENT]: DRAWER TRIGGER
 ******************************************/

class LTADrawerTrigger extends HTMLElement {
	constructor() {
		super()
	}

	connectedCallback() {
		// Elements
		this.drawerEl = document.querySelector(this.dataset.selector)

		// Events
		if (this.drawerEl) {
			this.addEventListener('click', () => {
				this.drawerEl?.drawer_open?.(this)
			})
		}
	}
}

customElements.define('l-drawer-trigger', LTADrawerTrigger)

/*****************************************
 * [COMPONENT]: QUANTITY INPUT
 ******************************************/

if (!customElements.get('l-qty-input')) {
	class LTAQtyInput extends HTMLElement {
		constructor() {
			super()

			this.onButtonClick = this.onButtonClick.bind(this)
			this.onInputChange = this.onInputChange.bind(this)
			this.onInputBeforeChange = this.onInputBeforeChange.bind(this)
		}

		connectedCallback() {
			this.input = this.querySelector('input')
			this.buttonMinus = this.querySelector("button[name='minus']")
			this.buttonPlus = this.querySelector("button[name='plus']")

			if (!this.input) return

			this.changeEvent = new Event('change', { bubbles: true })

			this.validateQtyRules()
			this.setupEventListeners()
		}

		setupEventListeners() {
			this.input.addEventListener('input', this.onInputBeforeChange)
			this.input.addEventListener('change', this.onInputChange)
			this.querySelectorAll('button').forEach((button) => button.addEventListener('click', this.onButtonClick))
		}

		onInputBeforeChange() {
			const value = parseInt(this.input.value)

			if (isNaN(value)) return

			const min = parseInt(this.input.min)
			const max = parseInt(this.input.max)
			let newValue = value

			if (!isNaN(min) && value < min) {
				newValue = min
			}

			if (!isNaN(max) && value > max) {
				newValue = max
			}

			if (newValue !== value) {
				this.input.value = newValue
			}
		}

		onInputChange() {
			this.validateQtyRules()
		}

		onButtonClick(event) {
			event.preventDefault()

			const previousValue = this.input.value
			const buttonName = event.target.name

			if (buttonName === 'plus') {
				this.incrementQuantity()
			} else if (buttonName === 'minus') {
				this.decrementQuantity()
			}

			if (previousValue !== this.input.value) {
				this.input.dispatchEvent(this.changeEvent)
				this.onQuantityChanged(previousValue, parseInt(this.input.value))
			}
		}

		onQuantityChanged(oldValue, newValue) {
			// Override in child classes if needed
		}

		incrementQuantity() {
			const min = parseInt(this.input.dataset.min)
			const step = parseInt(this.input.step)
			const value = parseInt(this.input.value)

			if (min > step && value === 0) {
				this.input.value = min
			} else {
				this.input.stepUp()
			}
		}

		decrementQuantity() {
			this.input.stepDown()

			const min = this.input.dataset.min
			if (min && min === this.input.value) {
				this.input.value = parseInt(this.input.min)
			}
		}

		validateQtyRules() {
			const value = parseInt(this.input.value)

			if (this.input.min && this.buttonMinus) {
				this.buttonMinus.toggleAttribute('disabled', value <= parseInt(this.input.min))
			}

			if (this.input.max && this.buttonPlus) {
				this.buttonPlus.toggleAttribute('disabled', value >= parseInt(this.input.max))
			}
		}

		disconnectedCallback() {
			if (this.input) {
				this.input.removeEventListener('input', this.onInputBeforeChange)
				this.input.removeEventListener('change', this.onInputChange)
			}

			this.querySelectorAll('button').forEach((button) => button.removeEventListener('click', this.onButtonClick))
		}
	}

	customElements.define('l-qty-input', LTAQtyInput)
}

/*****************************************
 * [COMPONENT]: ATC BUTTON
 ******************************************/

class LTACTAButton extends HTMLElement {
	constructor() {
		super()

		this.handleAtcBtnClick = this.handleAtcBtnClick.bind(this)
		this.handleQtyInputChange = this.handleQtyInputChange.bind(this)
	}

	connectedCallback() {
		this.buttonEl = this.querySelector('button[data-atc]')
		this.qtyInputEl = this.querySelector('l-qty-input [data-quantity-input]')

		this.buttonEl?.addEventListener('click', this.handleAtcBtnClick)
		this.qtyInputEl?.addEventListener('change', this.handleQtyInputChange)
	}

	disconnectedCallback() {
		this.buttonEl?.removeEventListener('click', this.handleAtcBtnClick)
		this.qtyInputEl?.removeEventListener('change', this.handleQtyInputChange)
	}

	handleAtcBtnClick() {
		if (!this.buttonEl) return

		this.buttonEl.classList.add('is-loading', 'l-button-loading')

		const { variantId, quantity, sellingPlanId } = this.dataset
		const config = {
			id: variantId,
			quantity: parseInt(quantity),
			...(sellingPlanId ? { selling_plan: sellingPlanId } : {}),
		}

		const formData = ltaCartService.createConfigFromItems([config])

		ltaCartService
			.addToCart(formData, 'l-atc-button')
			.then(console.log)
			.catch(console.error)
			.finally(() => {
				this.buttonEl.classList.remove('is-loading', 'l-button-loading')
			})
	}

	handleQtyInputChange() {
		if (!this.qtyInputEl || !this.buttonEl) return
		const currentQuantity = parseInt(this.qtyInputEl.value)

		this.dataset.quantity = currentQuantity

		if (currentQuantity > 0) {
			if (!this.buttonEl.hasAttribute('data-sold-out')) {
				this.buttonEl.removeAttribute('disabled')
			}
		} else {
			this.buttonEl.setAttribute('disabled', 'disabled')
		}
	}
}

customElements.define('l-atc-button', LTACTAButton)

/*****************************************
 * [COMPONENT]: AGE VERIFICATION POPUP
 ******************************************/

if (!customElements.get('l-age-verification-popup')) {
	class LTAAgeVerificationPopup extends customElements.get('l-drawer') {
		connectedCallback() {
			// Elements
			this.yesButtonEl = this.querySelector('[data-age-verification-popup-yes]')

			// Inherit from parent
			super.connectedCallback()

			// Design mode scroll
			if (Shopify.designMode) {
				this.handleDesignMode()
			} else {
				// Events
				this.togglePopup()
			}

			this.yesButtonEl.addEventListener('click', this.handleYesButtonClick.bind(this))
		}

		handleYesButtonClick() {
			sessionStorage.setItem('age_verification', 'true')
			this.togglePopup()
		}

		togglePopup() {
			const isUserVerified = sessionStorage.getItem('age_verification') === 'true'

			if (isUserVerified) {
				super.drawer_close?.()
				sessionStorage.setItem('age_verification', 'true')
			} else {
				super.drawer_open?.(this.triggerEl)
				sessionStorage.setItem('age_verification', 'false')
			}
		}

		handleDesignMode() {
			document.addEventListener('shopify:section:select', (event) => {
				const { sectionId } = event.detail

				sectionId == this.dataset.sectionId ? this.drawer_open() : this.drawer_close()
			})
		}
	}

	customElements.define('l-age-verification-popup', LTAAgeVerificationPopup)
}

/*****************************************
 * [COMPONENT]: OUR STORY TAB
 ******************************************/

if (!customElements.get('l-our-story-tab')) {
	class LTACollapsibleTabVertical extends HTMLElement {
		connectedCallback() {
			this.triggerEl = this.querySelector('[data-trigger]')
			this.contentEl = this.querySelector('[data-content]')

			if (this.triggerEl && this.contentEl) {
				this.triggerEl.addEventListener('click', () => this.toggle())
			}
		}

		toggle() {
			const isExpanded = this.classList.contains('is-expanded')

			isExpanded ? this.collapse() : this.expand()
		}

		expand() {
			this.contentEl.classList.remove('l-hidden')

			requestAnimationFrame(() => {
				this.classList.add('is-expanding')

				const onTransitionEnd = (event) => {
					if (event.target === this.contentEl) {
						this.contentEl.removeEventListener('transitionend', onTransitionEnd)
						this.classList.remove('is-expanding')
						this.classList.add('is-expanded')
						this.updateCarousel()
					}
				}

				this.contentEl.addEventListener('transitionend', onTransitionEnd)
			})
		}

		collapse() {
			// this.classList.add('is-expanding')
			this.classList.remove('is-expanded')

			l_debounce(() => {
				this.contentEl.classList.add('l-hidden')
				this.updateCarousel()
			}, 400)()
		}

		updateCarousel() {
			const swiperEl = this.closest('l-swiper-carousel')

			swiperEl.swiperCarousel.update()
			// swiperEl?.swiperCarousel?.update?.()
		}
	}

	customElements.define('l-our-story-tab', LTACollapsibleTabVertical)
}

/*****************************************
 * [COMPONENT]: OUR STORY MEDIA
 ******************************************/

if (!customElements.get('l-our-story-media')) {
	class LTAOurStoryMedia extends HTMLElement {
		constructor() {
			super()
			this._swiperInitialized = false
		}

		connectedCallback() {
			document.addEventListener('l:vendor:loaded', () => {
				this.initGSAPAnimations()
			})
		}

		initGSAPAnimations() {
			const headingEls = this.querySelectorAll('.js-gsap-heading')
			const contentEls = this.querySelectorAll('.js-gsap-content')
			const allHeadingLines = []

			headingEls.forEach((heading) => {
				const split = new SplitText(heading, {
					type: 'lines',
					linesClass: 'line',
				})

				gsap.set(split.lines, {
					opacity: 0,
					yPercent: 100,
				})

				allHeadingLines.push(...split.lines)
			})

			gsap.set(contentEls, { opacity: 0, xPercent: 20 })

			const tl = gsap.timeline({
				scrollTrigger: {
					trigger: this,
					start: 'top top+=30%',
					end: 'top top',
					scrub: 1.2,
					invalidateOnRefresh: true,
					onUpdate: (self) => {
						if (self.progress === 1) this.initCarousel()
					},
				},
			})

			tl.fromTo(
				this,
				{
					'--clip-path': 'inset(40% 12% 40% 12% round 12px)',
				},
				{
					'--clip-path': 'inset(20% 6% 20% 6% round 12px)',
					ease: 'power1.out',
					duration: 1,
				}
			)
				.to(this, {
					'--clip-path': 'inset(0% 0% 0% 0% round 12px)',
					ease: 'power2.out',
					duration: 1,
				})
				.to(
					allHeadingLines,
					{
						opacity: 1,
						yPercent: 0,
						stagger: {
							each: 0.08,
							from: 'start',
						},
						ease: 'power2.out',
					},
					'<0.2'
				)
				.to(
					contentEls,
					{
						opacity: 1,
						xPercent: 0,
						stagger: 0.1,
						ease: 'power2.out',
					},
					'<'
				)
		}

		initCarousel() {
			if (this._swiperInitialized) return

			const firstSlideEl = this.querySelector('.swiper-slide:first-child')
			if (!firstSlideEl) return

			const firstSlideHeight = firstSlideEl.offsetHeight
			this.style.height = `${firstSlideHeight}px`

			const configNode = this.querySelector('[data-carousel-config]')
			if (!configNode) return

			this.carouselConfig = JSON.parse(configNode.textContent)
			if (!this.carouselConfig) return

			this.swiperSlideEls = this.querySelectorAll('.swiper-slide')
			this.classList.remove('is-placeholder')
			this.swiperSlideEls.forEach((swiperSlide) => swiperSlide.classList.remove('l-hidden'))

			this.swiperCarousel = new window.Swiper(this, this.carouselConfig)
			this._swiperInitialized = true
		}
	}

	customElements.define('l-our-story-media', LTAOurStoryMedia)
}

/*****************************************
 * [COMPONENT]: FEATURED PRODUCTS TABS
 ******************************************/

if (!customElements.get('l-featured-products-tabs')) {
	class LTAFeaturedProductsTabs extends HTMLElement {
		constructor() {
			super()

			this._handleTriggerClick = this._handleTriggerClick.bind(this)
		}

		connectedCallback() {
			this.triggerEls = Array.from(this.querySelectorAll('[data-trigger]'))
			this.contentEls = Array.from(this.querySelectorAll('[data-content]'))

			this.triggerEls.forEach((trigger) => trigger.addEventListener('click', this._handleTriggerClick))
		}

		disconnectedCallback() {
			if (this.triggerEls) {
				this.triggerEls.forEach((trigger) => trigger.removeEventListener('click', this._handleTriggerClick))
			}
		}

		_handleTriggerClick(event) {
			const trigger = event.currentTarget || event.target
			const triggerId = trigger?.dataset?.trigger
			if (!triggerId) return

			this._setActiveTab(triggerId)
		}

		_setActiveTab(selectedTriggerId) {
			this.triggerEls.forEach((trigger) => {
				trigger.classList.toggle('is-active', trigger.dataset.trigger === selectedTriggerId)
			})

			this.contentEls.forEach((contentEl) => {
				const isActive = contentEl.dataset.content === selectedTriggerId

				contentEl.querySelectorAll('[data-aos]').forEach((aosEl) => {
					aosEl.classList.remove('aos-animate')

					requestAnimationFrame(() => {
						aosEl.classList.add('aos-animate')
					})
				})
				contentEl.classList.toggle('l-hidden', !isActive)
			})
		}

		_activateAos(contentEl) {
			// Remove existing aos classes
			contentEl.querySelectorAll('l-product-card').forEach((productCard) => {
				productCard.classList.remove('aos-init', 'aos-animate')
				productCard.setAttribute('data-aos', 'fade-up')
			})

			// Add them again after 2 seconds
			l_debounce(() => {
				contentEl.querySelectorAll('l-product-card').forEach((productCard) => {
					productCard.classList.add('aos-init', 'aos-animate')
				})
			}, 100)()
		}
	}

	customElements.define('l-featured-products-tabs', LTAFeaturedProductsTabs)
}

/*****************************************
 * [SECTION]: MAIN COLLECTION GROUPS
 ******************************************/

class LTAMainCollectionGroups extends HTMLElement {
	constructor() {
		super()

		this.handleTagClick = this.handleTagClick.bind(this)
	}

	connectedCallback() {
		// Elements
		this.tagsWrapperEl = this.querySelector('[data-tags]')
		this.groupEls = Array.from(this.querySelectorAll('[data-group]'))

		// Events
		if (this.tagsWrapperEl) {
			this.removeTagDelegate = l_addEventDelegate({
				context: this.tagsWrapperEl,
				event: 'click',
				selector: '[data-tag]',
				handler: this.handleTagClick,
			})
		}

		document.addEventListener('l:one-nation:header:tag:click', this.handleOneNationHeaderTagClick.bind(this))
	}

	disconnectedCallback() {
		if (this.removeTagDelegate) {
			this.removeTagDelegate()
		}
	}

	handleOneNationHeaderTagClick(e) {
		const { target } = e.detail

		this.handleTagClick(target)
	}

	handleTagClick(target) {
		const blockId = target?.dataset?.tag
		const showAll = blockId === 'all'

		this.tagsWrapperEl.querySelectorAll('[data-tag]').forEach((tagEl) => {
			tagEl.classList.toggle('is-active', target == tagEl)
		})

		this.groupEls.forEach((groupEl) => {
			const isActive = showAll || groupEl.id === blockId
			groupEl.classList.toggle('l-hidden', !isActive)

			if (isActive) {
				const animateEls = groupEl.querySelectorAll('.aos-init')
				animateEls.forEach((animateEl) => {
					animateEl.classList.remove('aos-animate')

					requestAnimationFrame(() => {
						animateEl.classList.add('aos-animate')
					})
				})
			}
		})
	}
}

customElements.define('l-main-collection-groups', LTAMainCollectionGroups)

/*****************************************
 * [SECTION]: ONE NATION HEADER
 ******************************************/

class LTAOneNationHeader extends HTMLElement {
	constructor() {
		super()
	}

	connectedCallback() {
		this.tagsWrapperEl = this.querySelector('[data-tags]')

		this.tagsWrapperEl.querySelectorAll('[data-tag]').forEach((tagEl) => {
			tagEl.addEventListener('click', this.handleTagClick.bind(this, tagEl))
		})
	}

	handleTagClick(tagEl) {
		const blockId = tagEl?.dataset?.tag

		this.tagsWrapperEl.querySelectorAll('[data-tag]').forEach((_tagEl) => {
			_tagEl.classList.toggle('is-active', _tagEl == tagEl)
		})

		document.dispatchEvent(
			new CustomEvent('l:one-nation:header:tag:click', {
				detail: {
					target: tagEl,
					blockId,
				},
			})
		)
	}
}

customElements.define('l-one-nation-header', LTAOneNationHeader)

/*****************************************
 * [SECTION]: MAIN COLLECTION GROUPS
 ******************************************/

class LTAShopCarousel extends customElements.get('l-swiper-carousel') {
	setupCarousel() {
		super.setupCarousel()

		this.productNameEls = this.querySelectorAll('[data-product-name]')
		this.shopAllLinkEls = this.querySelectorAll('[data-shop-all-link]')
		this.singleLinkEls = this.querySelectorAll('[data-block-link]')

		this.swiperCarousel.on('sliderMove', () => {
			this.classList.add('is-transitioning')
		})

		this.swiperCarousel.on('touchStart', () => {
			this.classList.add('is-transitioning')
		})

		this.swiperCarousel.on('touchEnd', (swiper, event) => {
			l_debounce(() => {
				const activeBlockId = this.querySelector('[data-block-slide].swiper-slide-active')?.dataset.blockId

				console.log(swiper.slides[swiper.realIndex])
				console.log(swiper.realIndex)

				this.productNameEls.forEach((productNameEl) => {
					productNameEl.classList.toggle('l-hidden', productNameEl.dataset.productName !== activeBlockId)
				})

				this.shopAllLinkEls.forEach((shopAllLinkEl) => {
					shopAllLinkEl.classList.toggle('l-hidden', shopAllLinkEl.dataset.shopAllLink !== activeBlockId)
				})

				this.singleLinkEls.forEach((singleLinkEl) => {
					console.log({ blockId: singleLinkEl.dataset.blockLink, activeBlockId })

					singleLinkEl.classList.toggle('is-showing', singleLinkEl.dataset.blockLink == activeBlockId)
				})

				l_debounce(() => {
					this.classList.remove('is-transitioning')
				}, 100)()
			}, 400)()
		})
	}
}

customElements.define('l-shop-carousel', LTAShopCarousel)

/*****************************************
 * [SECTION]: MAIN COLLECTION GROUPS
 ******************************************/

class LTAPopularProducts extends HTMLElement {
	constructor() {
		super()

		this.handleCarouselLoaded = l_debounce(this.setupCarousels.bind(this), 100)
	}

	connectedCallback() {
		this.productNameEls = this.querySelectorAll('[data-product-name]')
		this.activeProductPriceEl = this.querySelector('[data-price]')
		this.buyNowEl = this.querySelector('[data-buy-now]')

		this.paginationBulletEls = Array.from(this.querySelectorAll('[data-pagination-bullet]'))
		this.addEventListener('l:swiper:carousel:loaded', this.handleCarouselLoaded)
		this.addPaginationListeners()
	}

	setupCarousels() {
		this.removeEventListener('l:swiper:carousel:loaded', this.handleCarouselLoaded)

		const mainCarouselEl = this.querySelector('[data-main-carousel]')
		const thumbnailCarouselEl = this.querySelector('[data-thumbnails]')

		if (!mainCarouselEl || !thumbnailCarouselEl) return

		this.mainCarousel = mainCarouselEl.swiperCarousel
		this.thumbnailCarousel = thumbnailCarouselEl.swiperCarousel

		this.mainCarousel.on('slideChange', this.handleMainSlideChange.bind(this))
		this.thumbnailCarousel.on('slideChange', this.handleThumbnailSlideChange.bind(this))
	}

	handleMainSlideChange() {
		const { slides, activeIndex } = this.mainCarousel
		const totalSlides = slides.length
		const thumbnailIndex = activeIndex + 1 >= totalSlides ? 0 : activeIndex + 1

		this.currentThumbnailIndex = thumbnailIndex

		this.thumbnailCarousel.slideToLoop(thumbnailIndex)

		this.paginationBulletEls.forEach((bulletEl, idx) => {
			bulletEl.classList.toggle('is-active', idx === activeIndex)
		})

		// Update product data
		const activeSlideEl = slides[activeIndex]
		if (activeSlideEl) {
			this.activeProductPriceEl.textContent = activeSlideEl.dataset.productPrice
			this.buyNowEl.href = activeSlideEl.dataset.productUrl
		}

		// Update product titles
		console.log(activeIndex)

		this.productNameEls.forEach((productNameEl, index0) => {
			productNameEl.classList.toggle('l-hidden', activeIndex != index0)
		})
	}

	handleThumbnailSlideChange() {
		const thumbnailIndex = this.thumbnailCarousel.realIndex
		const mainCarouselSlideCount = this.mainCarousel.slides.length
		const mainCarouselIndex = thumbnailIndex === 0 ? mainCarouselSlideCount - 1 : thumbnailIndex - 1

		this.mainCarousel.slideTo(mainCarouselIndex)
	}

	addPaginationListeners() {
		this.paginationBulletEls.forEach((bulletEl, idx) => {
			bulletEl.addEventListener('click', () => {
				this.mainCarousel.slideTo(idx)
			})
		})
	}
}

customElements.define('l-section-popular-products', LTAPopularProducts)

/*****************************************
 * [GSAP]: MEDIA CONTENT WIDTHS
 ******************************************/

function l_animateMediaContentWidths() {
	const mediaContentEl = document.getElementById('l-media-content-widths')
	if (!mediaContentEl) return

	const mediaEl = mediaContentEl.querySelector('[data-media]')
	const contentEl = mediaContentEl.querySelector('[data-content]')
	if (!mediaEl || !contentEl) return

	const fixedHeight = mediaContentEl.offsetHeight
	mediaContentEl.style.height = fixedHeight + 'px'

	gsap.set(mediaEl, { '--l-col-w-desktop': '100%' })
	gsap.set(contentEl, {
		'--l-col-w-desktop': '0%',
		opacity: 0,
		visibility: 'hidden',
		pointerEvents: 'none',
	})

	const observer = new IntersectionObserver(
		([entry]) => {
			if (!entry.isIntersecting) return

			const tl = gsap.timeline({
				defaults: { ease: 'power2.out' },
				onComplete: () => {
					mediaContentEl.style.height = ''
					gsap.set(contentEl, {
						visibility: 'visible',
						pointerEvents: 'auto',
					})
					gsap.to(contentEl, {
						opacity: 1,
						duration: 0.4,
					})
				},
			})

			tl.to(
				mediaEl,
				{
					'--l-col-w-desktop': '50%',
					duration: 1.4,
				},
				0
			).to(
				contentEl,
				{
					'--l-col-w-desktop': '50%',
					duration: 1.4,
				},
				0
			)

			observer.disconnect()
		},
		{
			root: null,
			threshold: 0.5,
		}
	)

	observer.observe(mediaContentEl)
}

document.addEventListener('DOMContentLoaded', () => {
	l_animateMediaContentWidths()
})

